import store from '@/store'
import { CRM_PERMISSIONS } from '@/constants/permissions'

/**
 * 取得 router 實例（延遲載入，避免循環依賴）
 * @returns {VueRouter}
 */
function getRouter() {
  // 延遲 import，避免與 router.js 的循環依賴
  return require('@/router').default
}

/**
 * 取得可用的權限列表（優先從 store，fallback 用 CRM_PERMISSIONS）
 * @returns {Array}
 */
function getAvailablePermissions() {
  const storePermissions = store.getters.availablePermissions

  // 如果 store 有資料，使用 store 的（從 API 載入）
  if (storePermissions && storePermissions.length > 0) {
    return storePermissions
  }

  // Fallback: 使用本地常數
  console.warn('[Permission] Using CRM_PERMISSIONS as fallback')
  return CRM_PERMISSIONS.map((p, index) => ({
    id: index + 1,
    name: p.name,
    code_name: p.code_name
  }))
}

/**
 * 檢查用戶是否有權限訪問指定路由
 * @param {string} routeName - 路由名稱
 * @returns {boolean}
 */
export function canAccess(routeName) {
  return store.getters.canAccess(routeName)
}

/**
 * 檢查用戶是否為主權限
 * @returns {boolean}
 */
export function isRoot() {
  return store.getters.isRoot
}

/**
 * 取得用戶的所有權限
 * @returns {string[]}
 */
export function getRoutePermissions() {
  return store.getters.routePermissions
}

/**
 * 建立路由名稱到頂層父路由的對應表
 * 遍歷整個 router，找出所有子路由對應的頂層父路由（用於權限檢查）
 *
 * @returns {Object} - { 子路由名稱: 頂層父路由名稱 }
 */
function buildRouteParentMap() {
  const map = {}
  const availablePermissions = getAvailablePermissions()
  const permissionCodes = availablePermissions.map(p => p.code_name)
  const router = getRouter()

  function traverse(routes, topParent = null) {
    routes.forEach(route => {
      if (!route.name) return

      // 判斷是否為「頂層父路由」（有權限控制的那層）
      const isTopParent = permissionCodes.includes(route.name)

      // 更新當前的頂層父路由
      const currentTopParent = isTopParent ? route.name : topParent

      // 如果有頂層父路由，則記錄對應關係
      if (currentTopParent && route.name !== currentTopParent) {
        map[route.name] = currentTopParent
      }

      // 遞迴處理子路由
      if (route.children && route.children.length > 0) {
        traverse(route.children, currentTopParent)
      }
    })
  }

  traverse(router.options.routes)
  return map
}

// 快取對應表（延遲初始化）
let routeParentMapCache = null

/**
 * 取得路由父子關係對應表（有快取機制）
 */
function getRouteParentMap() {
  if (!routeParentMapCache) {
    routeParentMapCache = buildRouteParentMap()
  }
  return routeParentMapCache
}

/**
 * 根據路由名稱找到對應的父路由權限名稱
 *
 * 例如：用戶訪問子路由 'messageSend'，應該檢查父路由 'message' 的權限
 *
 * @param {string} routeName - 當前路由名稱
 * @returns {string} - 應該檢查的權限名稱
 */
export function getParentPermissionName(routeName) {
  // 使用動態建立的對應表（從 router 萃取）
  const routeParentMap = getRouteParentMap()
  if (routeParentMap[routeName]) {
    return routeParentMap[routeName]
  }

  // 如果是父路由或獨立路由，直接回傳自己
  return routeName
}

/**
 * 取得用戶第一個有權限的路由
 * 用於權限檢查失敗後的重定向
 *
 * @returns {string|null} - 路由路徑（例如 '/', '/patients'），如果沒有任何權限回傳 null
 */
export function getFirstAccessibleRoute() {
  // 主權限帳號直接回到首頁
  if (isRoot()) {
    return '/'
  }

  const permissions = getRoutePermissions()

  // 如果沒有任何權限，回傳 null（避免無窮回圈）
  if (!permissions || permissions.length === 0) {
    console.error('[Permission] 用戶沒有任何權限！')
    return null
  }

  const router = getRouter()
  const availablePermissions = getAvailablePermissions()

  // 按照優先順序檢查權限（使用從 API 載入的權限列表）
  for (const permission of availablePermissions) {
    if (permissions.includes(permission.code_name)) {
      // 使用 router.resolve 取得路由路徑
      const route = router.resolve({ name: permission.code_name })
      if (route && route.href) {
        return route.href
      }
    }
  }

  // 如果權限列表不為空，但沒有匹配到任何已定義的權限
  // 這種情況不應該發生，但作為保護機制
  console.error('[Permission] 權限列表不為空，但沒有匹配到任何已定義的路由', permissions)
  return null
}

/**
 * 檢查當前路由是否有權限訪問
 * @param {Object} route - Vue Router 的 route 物件
 * @returns {boolean}
 */
export function canAccessRoute(route) {
  const routeName = route.name

  // 特殊頁面：登入頁、切換帳號頁，不需要權限
  if (['login', 'switchAccount'].includes(routeName)) {
    return true
  }

  // 特殊頁面：帳號權限管理頁，只有 root 可以進
  if (['SubAccountSettings', 'PhonePermissionSettings', 'systemSettingsIndex', 'accountSettings'].includes(routeName)) {
    return isRoot()
  }

  // 找到應該檢查的權限名稱（父路由）
  const permissionName = getParentPermissionName(routeName)

  // 檢查是否有權限
  return canAccess(permissionName)
}

/**
 * Vue Mixin：在組件中使用權限檢查
 *
 * 使用方式：
 * export default {
 *   mixins: [permissionMixin],
 *   methods: {
 *     onClick() {
 *       if (this.canAccess('patients')) {
 *         // 有權限
 *       }
 *     }
 *   }
 * }
 */
export const permissionMixin = {
  computed: {
    $isRoot() {
      return isRoot()
    },
    $routePermissions() {
      return getRoutePermissions()
    }
  },
  methods: {
    $canAccess(routeName) {
      return canAccess(routeName)
    }
  }
}
